import './VoicemailForm.scss';

import { Button, styled, Typography } from '@mui/material';
import { FieldWrapper } from './Styles';
import { useController, useForm } from 'react-hook-form';
import axios from 'axios';
import { getEnvironment } from 'toolkit/environment/environment/environment';
import { getToken } from 'redux/utils/firebase-auth';
import { useEffect, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AudioPlayer from 'toolkit/media/audio-player';
import { userAdminSelectors } from 'redux/slices/userAdminSlice';
import {
  FILE_SIZE_LIMIT,
  SUPPORTED_FILE_TYPES,
  TemporaryLinkResponse,
} from '@act/shared/data-transfer-objects';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from 'react-components/app/ReusableToast';

type VoicemailFormProps = {
  userId: string;
  userVoicemailFileId?: string;
};

const FileSchema = z
  .union([
    z.instanceof(File),
    z.object({ name: z.string(), type: z.string(), size: z.number() }),
  ])
  .refine(file => SUPPORTED_FILE_TYPES.includes(file.type), {
    message: 'Invalid audio file type',
  })
  .refine(file => file.size <= FILE_SIZE_LIMIT, {
    message: 'File size should not exceed 5MB',
  });

export const AUDIO_SCHEMA = z.object({ file: FileSchema });
export type AudioSchemaType = z.infer<typeof AUDIO_SCHEMA>;

type FileUploadStatus =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'success'
  | 'error'
  | 'unsupported';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FileInput = ({ control, name, submit, isSingle = false }) => {
  const { field } = useController({ control, name });
  const [value, setValue] = useState('');

  return (
    <VisuallyHiddenInput
      name={name}
      type="file"
      value={value}
      onChange={e => {
        setValue(e.target.value);
        const files = e.target.files;
        field.onChange(isSingle ? files[0] : files);
        submit();
      }}
    />
  );
};

const VoicemailForm = ({ userId, userVoicemailFileId }: VoicemailFormProps) => {
  const [status, setStatus] = useState<FileUploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const guide = userAdminSelectors.useSelectAdminUserById(userId);
  const toast = useToast();

  const [uploadedFile, setUploadedFile] = useState<TemporaryLinkResponse>(null);

  const { handleSubmit, control, setValue } = useForm<AudioSchemaType>({
    resolver: zodResolver(AUDIO_SCHEMA),
    defaultValues: {
      file: null,
    },
  });

  const onSubmit = async (data: AudioSchemaType) => {
    setStatus('uploading');
    const baseUrl = getEnvironment().serviceUris.websocketService;
    const url = `${baseUrl}/files/upload-voicemail?userId=${userId}`;
    const token = await getToken();

    axios
      .post(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress: progressEvent => {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          setProgress(progress);
          // Once the file is uploaded, the status will change to processing
          // The server is still working on the file,
          // but the client is done uploading it
          if (progress === 100) {
            setStatus('processing');
          }
        },
      })
      .then(response => {
        setStatus('success');
        setUploadedFile(response.data.tempResponse);
        toast('Voicemail uploaded successfully');
      })
      .catch(() => {
        setProgress(0);
        setStatus('error');
      });
  };

  useEffect(() => {
    const resetStatusTypes = ['success', 'error', 'unsupported'];

    if (resetStatusTypes.includes(status)) {
      setTimeout(() => {
        // reset the form
        setStatus('idle');
        setProgress(0);
        setValue('file', null);
      }, 3000);
    }
  }, [status]);

  let buttonText = '';
  switch (status) {
    case 'idle':
      buttonText = 'Upload Voicemail Greeting';
      break;
    case 'uploading':
      buttonText = 'Uploading file...';
      break;
    case 'processing':
      buttonText = 'Processing file...';
      break;
    case 'success':
      buttonText = 'Upload Successful!';
      break;
    case 'error':
      buttonText = 'Error! Try again';
      break;
    case 'unsupported':
      buttonText = 'Unsupported file type or file size';
      break;
  }

  const voicemailFileId = guide?.voicemailFileId || userVoicemailFileId;
  const hasUploadedFile = uploadedFile || voicemailFileId;

  const playerRender = hasUploadedFile ? (
    <AudioPlayer
      fileId={uploadedFile ? undefined : voicemailFileId}
      tempFile={uploadedFile}
      key={uploadedFile ? uploadedFile.file.id : voicemailFileId}
      alwaysShowDownload={true}
    />
  ) : (
    <div className="no-file">
      <h3>No Uploaded Greeting</h3>
      <p>Upload an audio file to use as your voicemail greeting.</p>
    </div>
  );

  return (
    <FieldWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6">Voicemail Greeting</Typography>
        <div className="player">{playerRender}</div>
        <div className="full-rule"></div>
        <Button
          className={`upload-button button-state-${status}`}
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<CloudUploadIcon />}
          disabled={status !== 'idle'}
          sx={{
            display: 'block',
            margin: '0 auto',
          }}
        >
          <span>{buttonText}</span>
          <div className="progress-bar" style={{ width: `${progress}%` }}></div>
          <FileInput
            control={control}
            name="file"
            isSingle={true}
            submit={handleSubmit(onSubmit)}
          />
        </Button>
      </form>
    </FieldWrapper>
  );
};

export default VoicemailForm;
